import { AppError } from "@/lib/utils/errors";
import { routes } from "@/lib/utils/routes";
import { employeeService } from "@/modules/services/employeeService";
import {
  useEmployeeDegreeStore,
  useEmployeeInfoStore,
  useEmployeeExperienceStore,
  useEmployeeCertificateStore,
  useEmployeeCourseStore,
} from "@/modules/store/employeeStore";
import { redirect } from "next/navigation";
import { useAuth } from "../auth/useAuth";
import { useContext, useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import {
  EmployeeInfoRegister,
  EmployeeInfoUpdate,
  EmployeeInfoUpdateIntroduce,
} from "@/modules/types/employee/employee";
import { authService } from "@/modules/services/authService";
import { apiClient } from "@/lib/api/api-client";
import { EmployeeInfoContext } from "@/modules/components/auth/CheckAuthProvider/EmployeeInfoContext";
import { setAnalyticsUser } from "@/lib/analytics";

export const useEmployeeInfo = (strictCheck: boolean = false) => {
  const {
    status,
    employeeInfo: storeEmployeeInfo,
    setStatus,
    setEmployeeInfo,
    clearEmployeeInfo,
  } = useEmployeeInfoStore();
  const contextEmployeeInfo = useContext(EmployeeInfoContext);
  const employeeInfo = storeEmployeeInfo !== undefined ? storeEmployeeInfo : contextEmployeeInfo;

  const { logout: onLogout } = useAuth();
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const getEmployeeInfo = async () => {
    const svc = employeeService;
    setStatus("loading");
    try {
      const resp = await svc.getByDefault();
      if (resp && resp.employeeInfo) {
        setEmployeeInfo(resp.employeeInfo);
        const authId =
          (resp.employeeInfo as any)?.auth_id ||
          (resp.employeeInfo as any)?.authId ||
          resp.employeeInfo.id;
        if (authId) {
          setAnalyticsUser(authId);
        }
        setStatus("success");
      } else {
        setStatus("error");
        setEmployeeInfo(null);
      }
    } catch (err) {
      setStatus("error");
      setEmployeeInfo(null);
      const appErr = new AppError(err);
      appErr.setAction("useEmployeeInfo.getEmployeeInfo");
      appErr.setApiAction("employeeService.getByDefault");
      appErr.logError();
      // enqueueSnackbar(appErr.message, { variant: "error" });
      if (strictCheck) {
        redirect(routes.auth.login);
      }
    } finally {
      setTimeout(() => {
        setStatus("idle");
      }, 3000);
    }
  };

  const createEmployeeInfo = async (employeeInfo: EmployeeInfoRegister) => {
    setIsCreating(true);
    try {
      const resp = await employeeService.registerInfo(employeeInfo);
      if (resp) {
        await getEmployeeInfo();
      }
    } catch (err) {
      const appErr = new AppError(err);
      appErr.setAction("useEmployeeInfo.createEmployeeInfo");
      appErr.setApiAction("employeeService.registerInfo");
      appErr.logError();
      setCreateError(appErr.message);
    } finally {
      setIsCreating(false);
    }
  };

  const clearCreateError = () => {
    setCreateError(null);
  };

  const logout = async () => {
    await authService.logout();
    apiClient.setToken(null);
    onLogout();
    clearEmployeeInfo();
    setAnalyticsUser(null);
  };

  return {
    employeeInfo,
    loading: status === "loading",
    isCreating,
    createError,
    clearCreateError,
    createEmployeeInfo,
    getEmployeeInfo,
    setEmployeeInfo,
    logout,
  };
};

export const useAIGenSuggestionForJob = (jobId: string) => {
  // const { t } = useClientTranslations("job");
  const { employeeInfo } = useEmployeeInfo();
  const [suggestion, setSuggestion] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [openLoginModal, setOpenLoginModal] = useState(false);

  useEffect(() => {
    if (jobId) {
      setSuggestion(null);
    }
  }, [jobId]);

  const genSuggestion = async () => {
    if (!employeeInfo) {
      setOpenLoginModal(true);
      return;
    }
    try {
      setLoading(true);
      const svc = employeeService;
      const resp = await svc.aiGenSuggestionForJob(employeeInfo.id, jobId);
      if (resp) {
        setSuggestion(resp.suggestion);
      }
    } catch (err) {
      const appErr = new AppError(err);
      appErr.setAction("useAIGenSuggestionForJob.genSuggestion");
      appErr.setApiAction("employeeService.aiGenSuggestionForJob");
      appErr.logError();
    } finally {
      setLoading(false);
    }
  };

  const onCloseLoginModal = () => {
    setOpenLoginModal(false);
  };

  return {
    suggestion,
    loading,
    genSuggestion,
    openLoginModal,
    onCloseLoginModal,
  };
};

export const useChangeAvatar = () => {
  const { employeeInfo } = useEmployeeInfo();
  const [avatar, setAvatar] = useState(employeeInfo?.avatar);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { setEmployeeInfo } = useEmployeeInfoStore();

  useEffect(() => {
    setAvatar(employeeInfo?.avatar);
  }, [employeeInfo?.avatar]);

  const onOpen = () => {
    setIsOpen(true);
  };

  const onClose = () => {
    setIsOpen(false);
  };

  const uploadAvatar = async (file: File) => {
    if (!employeeInfo) return;
    setIsLoading(true);
    try {
      const extension = file.name.split(".").pop();
      const fileName = `avatar.${extension}`;
      const imageUrl = await employeeService.uploadAvatar(
        employeeInfo.id,
        file,
        fileName,
      );
      if (imageUrl) {
        const resp = await employeeService.updateAvatar(
          employeeInfo.id,
          imageUrl,
        );
        if (resp) {
          enqueueSnackbar("Logo uploaded successfully", { variant: "success" });
          setEmployeeInfo({ ...employeeInfo, avatar: imageUrl });
        } else {
          enqueueSnackbar("Failed to upload logo", { variant: "error" });
        }
      } else {
        enqueueSnackbar("Failed to upload logo", { variant: "error" });
      }
    } catch (err) {
      const appErr = new AppError(err);
      appErr.setAction("useChangeAvatar.uploadAvatar");
      appErr.setApiAction("employeeService.uploadAvatar/updateAvatar");
      appErr.logError(
        "src/modules/hooks/employee/useEmployeeInfo.ts[File][useChangeAvatar][uploadAvatar]",
      );
      enqueueSnackbar(appErr.message, { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    employeeInfo,
    avatar,
    isOpen,
    isLoading,
    onOpen,
    onClose,
    uploadAvatar,
  };
};

export const useUpdateEmployeeInfo = () => {
  const { employeeInfo, setEmployeeInfo } = useEmployeeInfo();
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState<EmployeeInfoUpdate>(
    {} as EmployeeInfoUpdate,
  );
  const [introduce, setIntroduce] = useState<string>("");
  const [currentIntroduce, setCurrentIntroduce] = useState<string>("");
  const [isIntroduceLoading, setIsIntroduceLoading] = useState(false);

  useEffect(() => {
    setIntroduce(employeeInfo?.introduce || "");
    setCurrentIntroduce(employeeInfo?.introduce || "");
  }, [employeeInfo?.introduce]);

  useEffect(() => {
    if (employeeInfo) {
      setForm({
        id: employeeInfo.id,
        firstName: employeeInfo.firstName,
        lastName: employeeInfo.lastName,
        email: employeeInfo.email,
        phone: employeeInfo.phone,
        birthDay: employeeInfo.birthDay,
        gender: employeeInfo.gender,
        title: employeeInfo.title,
        introduce: employeeInfo.introduce,
        avatar: employeeInfo.avatar,
        city: employeeInfo.city,
        province: employeeInfo.province,
        district: employeeInfo.district,
        ward: employeeInfo.ward,
        street: employeeInfo.street,
        targetCategoryId: employeeInfo.targetCategoryId || employeeInfo.target_category_id,
        // cvUploadedUrl: employeeInfo.cvUploadedUrl,
      });
    }
  }, [employeeInfo]);

  const handleChange = (field: string, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!employeeInfo || !form) return;
    setIsLoading(true);
    try {
      const resp = await employeeService.update(employeeInfo.id, form);
      if (resp) {
        enqueueSnackbar("Update employee info successfully", {
          variant: "success",
        });
        setEmployeeInfo({
          ...employeeInfo,
          ...form,
        });
      }
    } catch (err) {
      const appErr = new AppError(err);
      appErr.setAction("useUpdateEmployeeInfo.handleSave");
      appErr.setApiAction("employeeService.update");
      appErr.logError(
        "src/modules/hooks/employee/useEmployeeInfo.ts[File][useUpdateEmployeeInfo][handleSave]",
      );
      enqueueSnackbar(appErr.message, { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setForm(
      (employeeInfo &&
        ({
          id: employeeInfo.id,
          firstName: employeeInfo.firstName,
          lastName: employeeInfo.lastName,
          email: employeeInfo.email,
          phone: employeeInfo.phone,
          birthDay: employeeInfo.birthDay,
          gender: employeeInfo.gender,
          title: employeeInfo.title,
          introduce: employeeInfo.introduce,
          avatar: employeeInfo.avatar,
          city: employeeInfo.city,
          province: employeeInfo.province,
          district: employeeInfo.district,
          ward: employeeInfo.ward,
          street: employeeInfo.street,
          targetCategoryId: employeeInfo.targetCategoryId || employeeInfo.target_category_id,
          // cvUploadedUrl: employeeInfo.cvUploadedUrl,
        } as EmployeeInfoUpdate)) ||
      ({} as EmployeeInfoUpdate),
    );
  };

  const handleIntroduceChange = (value: string) => {
    setIntroduce(value);
  };

  const handleSaveIntroduce = async () => {
    if (!employeeInfo) return;
    if (introduce === currentIntroduce.trim()) {
      return;
    }
    setIsIntroduceLoading(true);
    try {
      const payload: EmployeeInfoUpdateIntroduce = {
        id: employeeInfo.id,
        introduce: introduce.trim(),
      };
      const resp = await employeeService.update(employeeInfo.id, payload);
      if (resp) {
        enqueueSnackbar("Update introduce successfully", {
          variant: "success",
        });
        setEmployeeInfo({
          ...employeeInfo,
          introduce: introduce,
        });
        setCurrentIntroduce(introduce);
      }
    } catch (err) {
      const appErr = new AppError(err);
      appErr.setAction("useUpdateEmployeeInfo.handleSaveIntroduce");
      appErr.setApiAction("employeeService.update");
      appErr.logError(
        "src/modules/hooks/employee/useEmployeeInfo.ts[File][useUpdateEmployeeInfo][handleSaveIntroduce]",
      );
      enqueueSnackbar(appErr.message, { variant: "error" });
    } finally {
      setIsIntroduceLoading(false);
    }
  };

  const handleCancelIntroduce = () => {
    setIntroduce(currentIntroduce);
  };

  return {
    employeeInfo,
    isLoading,
    form,
    handleChange,
    handleSave,
    handleCancel,
    introduce,
    handleIntroduceChange,
    handleSaveIntroduce,
    isIntroduceLoading,
    handleCancelIntroduce,
  };
};

export const useEmployeeDegrees = () => {
  // const [degrees, setDegrees] = useState<Degree[]>([]);
  const { employeeDegrees, setEmployeeDegrees } = useEmployeeDegreeStore();
  const { employeeInfo } = useEmployeeInfo();

  const getDegrees = async (employeeId: string, force: boolean = false) => {
    if (!employeeId) return;
    if (employeeDegrees.length > 0 && !force) return;
    try {
      const resp = await employeeService.getDegrees(employeeId);
      if (resp) {
        setEmployeeDegrees(resp.employeeDegree);
      }
    } catch (err) {
      const appErr = new AppError(err);
      appErr.setAction("useEmployeeDegrees.getDegrees");
      appErr.setApiAction("employeeService.getDegrees");
      appErr.logError(
        "src/modules/hooks/employee/useEmployeeInfo.ts[File][useEmployeeDegrees][getDegrees]",
      );
    }
  };

  useEffect(() => {
    if (employeeInfo) {
      getDegrees(employeeInfo.id, true);
    }
  }, [employeeInfo]);

  return {
    degrees: employeeDegrees,
    getDegrees,
  };
};

export const useEmployeeExperiences = () => {
  const { employeeExperiences, setEmployeeExperiences } =
    useEmployeeExperienceStore();
  const { employeeInfo } = useEmployeeInfo();

  const getExperiences = async (employeeId: string, force: boolean = false) => {
    if (!employeeId) return;
    if (employeeExperiences.length > 0 && !force) return;
    try {
      const resp = await employeeService.getExperiences(employeeId);
      if (resp) {
        setEmployeeExperiences(resp.experiences);
      }
    } catch (err) {
      const appErr = new AppError(err);
      appErr.setAction("useEmployeeExperiences.getExperiences");
      appErr.setApiAction("employeeService.getExperiences");
      appErr.logError(
        "src/modules/hooks/employee/useEmployeeInfo.ts[File][useEmployeeExperiences][getExperiences]",
      );
    }
  };

  useEffect(() => {
    if (employeeInfo) {
      getExperiences(employeeInfo.id, true);
    }
  }, [employeeInfo]);

  return {
    experiences: employeeExperiences,
    getExperiences,
  };
};

export const useEmployeeCertificates = () => {
  const { employeeCertificates, setEmployeeCertificates } =
    useEmployeeCertificateStore();
  const { employeeInfo } = useEmployeeInfo();

  const getCertificates = async (
    employeeId: string,
    force: boolean = false,
  ) => {
    if (!employeeId) return;
    if (employeeCertificates.length > 0 && !force) return;
    try {
      const resp = await employeeService.getCertificates(employeeId);
      if (resp) {
        setEmployeeCertificates(resp.employeeCertificates);
      }
    } catch (err) {
      const appErr = new AppError(err);
      appErr.setAction("useEmployeeCertificates.getCertificates");
      appErr.setApiAction("employeeService.getCertificates");
      appErr.logError(
        "src/modules/hooks/employee/useEmployeeInfo.ts[File][useEmployeeCertificates][getCertificates]",
      );
    }
  };

  useEffect(() => {
    if (employeeInfo) {
      getCertificates(employeeInfo.id, true);
    }
  }, [employeeInfo]);

  return {
    certificates: employeeCertificates,
    getCertificates,
  };
};

export const useEmployeeCourses = () => {
  const { employeeCourses, setEmployeeCourses } = useEmployeeCourseStore();
  const { employeeInfo } = useEmployeeInfo();

  const getCourses = async (employeeId: string, force: boolean = false) => {
    if (!employeeId) return;
    if (employeeCourses.length > 0 && !force) return;
    try {
      const resp = await employeeService.getCourses(employeeId);
      if (resp && resp.employeeCourses) {
        setEmployeeCourses(resp.employeeCourses);
      }
    } catch (err) {
      const appErr = new AppError(err);
      appErr.setAction("useEmployeeCourses.getCourses");
      appErr.setApiAction("employeeService.getCourses");
      appErr.logError(
        "src/modules/hooks/employee/useEmployeeInfo.ts[File][useEmployeeCourses][getCourses]",
      );
    }
  };

  useEffect(() => {
    if (employeeInfo) {
      getCourses(employeeInfo.id, true);
    }
  }, [employeeInfo]);

  return {
    courses: employeeCourses,
    getCourses,
  };
};
